'use client';

import type { FC } from 'react';
import type { FooterProps as FooterDataProps } from '@/ui/components/Footer/footer-types';
import useOrder from '@/core/hooks/use-order';
import useTranslations from '@/core/hooks/use-translations';
import { usePathname } from '@/core/i18n/routing';
import FooterNavigation from '@/ui/components/Footer/FooterNavigation';
import LogoLink from '@/ui/components/LogoLink/LogoLink';
import SocialLinks from '@/ui/components/SocialLinks/SocialLinks';
import Typography from '@/ui/components/Typography/Typography';

import $ from './Footer.module.scss';

export type FooterProps = {
  /** Footer data from Uniform composition. Navigation and social links render only when present in data. */
  footerData?: FooterDataProps | null;
};

const Footer: FC<FooterProps> = ({ footerData }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const { cart } = useOrder();
  const copyrightYear = new Date().getFullYear().toString();

  const isCartPage = pathname.includes('/cart');
  const isCheckoutPage = pathname.includes('/checkout');
  const isCartEmpty =
    !cart || (cart.products.length === 0 && cart.subscriptions.length === 0);
  const shouldShowTopContent = isCartEmpty || (!isCartPage && !isCheckoutPage);

  return (
    <footer className={$.footer}>
      {shouldShowTopContent && (
        <div className={$.topContent}>
          <div className={$.logoSocialsContainer}>
            <LogoLink renderOn="footer" />
            <SocialLinks
              title={footerData?.socialLinksTitle}
              items={footerData?.socialLinks}
            />
          </div>
          <FooterNavigation sections={footerData?.linkSections} />
        </div>
      )}

      <div className={$.bottomContent}>
        <Typography variant="bodyMedium" center className={$.copyright}>
          {t('web_footer_bottom_copyright', { year: copyrightYear }).replace(
            /\b20\d{2}\b/g,
            copyrightYear,
          )}
        </Typography>
      </div>
    </footer>
  );
};

export default Footer;
